import {
  LOCAL_STORAGE_LAST_ACTION_TIMESTAMP,
  LOCAL_STORAGE_USER_ID,
  LOCAL_STORAGE_USER_TOKEN,
} from 'UI/constants/localStorage';
import { clientLogger as logger, navigateExternal, navigateInternal } from 'UI/lib';
import { getIsInternalUrl } from 'UI/lib/getUrlType';
import { getUrlQueryParam } from 'UI/lib/helpers';
import {
  getOpportunityFilterStorageKey,
  OPPORTUNITY_FILTER_SUFFIXES,
} from 'UI/pages/restrict/opportunities/OpportunitiesFilterPanel/OpportunitiesFilterContext/useOrganizationFilterSuffix';

import { getApiHeaders } from '../getApiHeaders';
import getApolloClient from '../getApolloClient';
import getBackend from '../getBackend';
import {
  AUTH_DEFAULT_LOGIN_REDIRECT_URL,
  AUTH_ERROR_CODE_QUERY_PARAM,
  AUTH_ERROR_REASON_QUERY_PARAM,
} from './constants';
import getIsRestrictArea from './getIsRestrictArea';
import { loginSsoUser } from './loginSsoUser';

const getBackEndAuthUrl = path => `${getBackend()}/auth/${path}`;

const handleNavigation = redirectUrlOrPath => {
  const isInternalRedirect = getIsInternalUrl(redirectUrlOrPath);
  const navigate = isInternalRedirect ? navigateInternal : navigateExternal;
  navigate(redirectUrlOrPath);
};

export const getBrowserUserCredentials = () => {
  const palyxUserId = localStorage.getItem(LOCAL_STORAGE_USER_ID);
  const palyxToken = localStorage.getItem(LOCAL_STORAGE_USER_TOKEN);

  return {
    palyxUserId,
    palyxToken,
  };
};

export const setBrowserUserCredentials = userCredentials => {
  const { palyxUserId, palyxToken } = userCredentials;

  if (palyxUserId) {
    localStorage.setItem(LOCAL_STORAGE_USER_ID, palyxUserId);
  }

  if (palyxToken) {
    localStorage.setItem(LOCAL_STORAGE_USER_TOKEN, palyxToken);
  }
};

const removeUserStorage = () => {
  localStorage.removeItem(LOCAL_STORAGE_LAST_ACTION_TIMESTAMP);
  localStorage.removeItem(LOCAL_STORAGE_USER_ID);
  localStorage.removeItem(LOCAL_STORAGE_USER_TOKEN);

  for (const suffix in Object.values(OPPORTUNITY_FILTER_SUFFIXES)) {
    localStorage.removeItem(getOpportunityFilterStorageKey(suffix));
  }

  localStorage.removeItem('personalDevelopmentFilters');
};

/**
 * Logs out the user and redirect. If redirectOverride is provided, it will be used instead of the default redirect.
 * @param {string} redirectOverride - The redirect url or path. If false, redirect will be skipped.
 * @return {Promise.<void>}
 */
export const logoutUser = async redirectOverride => {
  const apolloClient = getApolloClient();
  const { palyxToken, palyxUserId } = getBrowserUserCredentials();
  const actions = [];

  if (palyxToken) {
    actions.push(
      fetch(getBackEndAuthUrl('user/logout'), {
        headers: getApiHeaders({ userId: palyxUserId, token: palyxToken }),
        method: 'POST',
      })
    );
  }

  if (apolloClient) {
    actions.push(apolloClient.clearStore());
  }

  const [logoutRes] = await Promise.all(actions);

  removeUserStorage();

  if (redirectOverride) {
    handleNavigation(redirectOverride);
    return true;
  }

  if (redirectOverride === false) {
    return;
  }

  if (logoutRes?.status === 200) {
    const body = await logoutRes.json();
    const isValidRedirectUrl = body?.redirectUrl?.startsWith('http');
    if (isValidRedirectUrl) {
      navigateExternal(body.redirectUrl);
      return true;
    }
  }

  navigateInternal('/logged-out');
  return true;
};

/**
 * @return {Promise.<void>}
 */
export const checkUserSession = async () => {
  const { pathname, search, hash, href } = window.location;
  const currentUrl = new URL(href);
  const isRestrict = getIsRestrictArea(currentUrl);
  const { palyxToken, palyxUserId } = getBrowserUserCredentials();
  const headers = getApiHeaders({ userId: palyxUserId, token: palyxToken });
  const isUserLogged = Boolean(palyxToken);

  if (isUserLogged) {
    const response = await fetch(getBackEndAuthUrl('user/info'), {
      headers,
    });

    if (response.ok) {
      if (!isRestrict) {
        navigateInternal(AUTH_DEFAULT_LOGIN_REDIRECT_URL);
        return true;
      }
    } else {
      // Unauthorized
      if (response.status === 401) {
        return await logoutUser(window.location.pathname);
      }

      return await logoutUser(`/?${AUTH_ERROR_CODE_QUERY_PARAM}=${response.status}`);
    }
  }

  const ssoCode = currentUrl.searchParams.get('code');
  const ssoState =
    currentUrl.searchParams.get('state') || encodeURIComponent(AUTH_DEFAULT_LOGIN_REDIRECT_URL);

  const isSsoCallback = Boolean(ssoCode?.trim());

  if (isSsoCallback) {
    return loginSsoUser({
      code: ssoCode,
      state: ssoState,
    });
  }

  const redirectUri =
    getUrlQueryParam('redirect_uri') ?? encodeURIComponent(`${pathname}${search}${hash}`);

  const ssoInit = await fetch(
    getBackEndAuthUrl(`sso/init${redirectUri ? `?redirectUri=${redirectUri}` : ''}`),
    { headers }
  );

  // No SSO redirect
  if (ssoInit.status === 204) {
    return;
  }

  if (ssoInit.status === 200) {
    try {
      const { location } = await ssoInit.json();
      const isValidLocation = location.includes('://');
      if (isValidLocation) {
        navigateExternal(location);
        return true;
      }
    } catch (error) {
      logger.error(error, `Error while parsing SSO init response: ${error.message}`);
    }
  }

  navigateInternal(
    `/?${AUTH_ERROR_CODE_QUERY_PARAM}=500&${AUTH_ERROR_REASON_QUERY_PARAM}=invalid SSO init response`
  );
  return true;
};
